// classificationHeaderSync.js
// Shared logic for syncing the document header's classification/distribution/compartment
// metadata whenever a block's classificationLevel attr changes — either from a manual
// gutter-menu edit (classificationGutterPlugin) or from a citation drop raising a block's
// floor to match its most-classified reference (snippetDropPlugin).

import {
    calculateDocumentMaxClassification,
    collectDocumentCompartments,
    collectDocumentDistributions,
    getClassificationRank,
} from './documentClassification';
import {getClassificationLabel} from './classificationLevels';
import {updateDocumentHeaderData} from '../firestore/documents';
import {multiSelectValueKeys, normalizeHeaderValue} from '../../../utils/documentHeaderMeta';

/**
 * Recompute and persist the document header's classification level/distribution/compartment
 * after a paragraph/heading node's classificationLevel attr has just been changed.
 *
 * @param {Object} params
 * @param {string|null} params.documentId
 * @param {import('yjs').Map|null} params.yDocMeta
 * @param {import('yjs').Doc|null} params.ydoc
 * @param {import('prosemirror-model').Node} params.committedDoc - the doc AFTER the change was dispatched
 * @param {string} params.appliedLevel - the classification level just applied to the changed node
 */
export function syncDocumentHeaderClassification({documentId, yDocMeta, ydoc, committedDoc, appliedLevel}) {
    if (!documentId) return;

    const currentHeaderLevel = yDocMeta?.get('classification')?.value || 'U';
    const currentHeaderRank = getClassificationRank(currentHeaderLevel);
    const newLevelRank = getClassificationRank(appliedLevel);

    let shouldUpdateClassification = false;
    let finalClassificationLevel = currentHeaderLevel;

    if (newLevelRank > currentHeaderRank) {
        // New level is HIGHER than header => use new level directly
        shouldUpdateClassification = true;
        finalClassificationLevel = appliedLevel;
    } else if (newLevelRank < currentHeaderRank) {
        // New level is LOWER than header => recalculate max from committed doc
        const maxLevel = calculateDocumentMaxClassification(committedDoc);
        if (maxLevel !== currentHeaderLevel) {
            shouldUpdateClassification = true;
            finalClassificationLevel = maxLevel;
        }
    }
    // Equal ranks => no classification update needed

    // Always recalculate distributions and compartments (they aggregate, not max)
    const allDistributions = collectDocumentDistributions(committedDoc);
    const allCompartments = collectDocumentCompartments(committedDoc);
    const normalizedDistributions = normalizeHeaderValue('distribution', allDistributions);
    const normalizedCompartments = normalizeHeaderValue('compartment', allCompartments);
    const currentDistributions = normalizeHeaderValue('distribution', yDocMeta?.get('distribution') || []);
    const currentCompartments = normalizeHeaderValue('compartment', yDocMeta?.get('compartment') || []);
    const nodeDistributionKeys = multiSelectValueKeys(normalizedDistributions, 'distribution');
    const headerDistributionKeys = multiSelectValueKeys(currentDistributions, 'distribution');
    const nodeCompartmentKeys = multiSelectValueKeys(normalizedCompartments, 'compartment');
    const headerCompartmentKeys = multiSelectValueKeys(currentCompartments, 'compartment');
    const distributionsChanged = JSON.stringify(nodeDistributionKeys) !== JSON.stringify(headerDistributionKeys);
    const compartmentsChanged = JSON.stringify(nodeCompartmentKeys) !== JSON.stringify(headerCompartmentKeys);

    // Only write if something actually changed
    if (!shouldUpdateClassification && !distributionsChanged && !compartmentsChanged) return;

    const updates = {};

    if (shouldUpdateClassification) {
        updates.classification = {
            value: finalClassificationLevel,
            label: getClassificationLabel(finalClassificationLevel),
        };
    }
    if (distributionsChanged) {
        if (nodeDistributionKeys.length > 0 || headerDistributionKeys.length === 0) {
            updates.distribution = normalizedDistributions;
        }
    }
    if (compartmentsChanged) {
        if (nodeCompartmentKeys.length > 0 || headerCompartmentKeys.length === 0) {
            updates.compartment = normalizedCompartments;
        }
    }

    // 1. Commit to Yjs FIRST so all collaborators see the change before the Firestore write fires.
    //    If Firestore fails, the Yjs state is already correct and the user is informed.
    if (yDocMeta && ydoc) {
        ydoc.transact(() => {
            if (shouldUpdateClassification) {
                yDocMeta.set('classification', updates.classification);
            }
            if (distributionsChanged && updates.distribution) {
                yDocMeta.set('distribution', updates.distribution);
            }
            if (compartmentsChanged && updates.compartment) {
                yDocMeta.set('compartment', updates.compartment);
            }
        }, 'classification-update');
    }

    // 2. Persist to Firestore after Yjs is committed.
    updateDocumentHeaderData(documentId, updates).catch((error) => {
        console.error('[classificationHeaderSync] Failed to update document header:', error);
        alert('Classification saved locally but failed to sync to server. Please check your connection.');
    });
}
